import { useCallback,useEffect, useState } from 'react'

import { getCachedData, setCachedData } from '@/lib/cache'

interface UseCachedDataOptions<T> {
  cacheKey: string
  maxAge: number
  fetchFn: () => Promise<T>
}

export function useCachedData<T>({ cacheKey, maxAge, fetchFn }: UseCachedDataOptions<T>) {
  const cachedData = getCachedData<T>(cacheKey, maxAge)
  const [data, setData] = useState<T | null>(cachedData)
  const [loading, setLoading] = useState(cachedData === null)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await fetchFn()
      setData(result)
      setCachedData(cacheKey, result)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch data'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [cacheKey, fetchFn])

  useEffect(() => {
    if (cachedData === null) {
      fetch()
    }
  }, [cachedData, fetch])

  return {
    data,
    loading,
    error,
    refetch: fetch,
    setData,
  }
}

